import { normalizeChordSymbol } from "@bandwith/chords";
import { Chord, Interval, Note } from "tonal";
import type { AudioSynth, ChordPlayOptions, NotePlayOptions } from "./types";

/**
 * Standard guitar tuning from low E to high E (strings 6 to 1)
 */
const GUITAR_TUNING = ["E2", "A2", "D3", "G3", "B3", "E4"];

/**
 * Web implementation of AudioSynth using Web Audio API.
 * Handles iOS Safari audio unlock and provides chord/note playback.
 */
export function createWebAudioSynth(): AudioSynth {
  let audioContext: AudioContext | null = null;
  let isAudioUnlocked = false;
  let unlockListenersAttached = false;
  let globalVolume = 0.3;
  const activeOscillators: OscillatorNode[] = [];

  function getAudioContext(): AudioContext {
    if (!audioContext || audioContext.state === "closed") {
      audioContext = new AudioContext();
      attachUnlockListeners();
      audioContext.addEventListener("statechange", handleStateChange);
    }
    return audioContext;
  }

  function handleStateChange(): void {
    if (!audioContext) return;
    const state = audioContext.state as string;
    if (state === "interrupted" || state === "suspended") {
      isAudioUnlocked = false;
      attachUnlockListeners();
    }
  }

  function playSilentBuffer(ctx: AudioContext): Promise<void> {
    return new Promise((resolve) => {
      const buffer = ctx.createBuffer(1, 1, 22050);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      source.onended = () => {
        isAudioUnlocked = true;
        resolve();
      };
      source.start(0);
      setTimeout(() => {
        isAudioUnlocked = true;
        resolve();
      }, 100);
    });
  }

  async function unlockAudio(): Promise<void> {
    if (isAudioUnlocked) return;
    const ctx = audioContext;
    if (!ctx) return;

    try {
      if (
        ctx.state === "suspended" ||
        ctx.state === ("interrupted" as AudioContextState)
      ) {
        await ctx.resume();
      }
      if (!isAudioUnlocked) {
        await playSilentBuffer(ctx);
      }
      if (isAudioUnlocked) {
        removeUnlockListeners();
      }
    } catch (e) {
      console.warn("Audio unlock failed:", e);
    }
  }

  function attachUnlockListeners(): void {
    if (unlockListenersAttached || typeof document === "undefined") return;
    // Use capture: true for iOS Safari audio unlock, passive: true for scroll performance
    document.addEventListener("touchstart", unlockAudio, {
      capture: true,
      passive: true,
    });
    document.addEventListener("touchend", unlockAudio, {
      capture: true,
      passive: true,
    });
    document.addEventListener("click", unlockAudio, { capture: true });
    document.addEventListener("keydown", unlockAudio, { capture: true });
    unlockListenersAttached = true;
  }

  function removeUnlockListeners(): void {
    if (!unlockListenersAttached || typeof document === "undefined") return;
    document.removeEventListener("touchstart", unlockAudio, { capture: true });
    document.removeEventListener("touchend", unlockAudio, { capture: true });
    document.removeEventListener("click", unlockAudio, { capture: true });
    document.removeEventListener("keydown", unlockAudio, { capture: true });
    unlockListenersAttached = false;
  }

  async function ensureAudioContextRunning(ctx: AudioContext): Promise<void> {
    const state = ctx.state as string;
    if (state === "suspended" || state === "interrupted") {
      await ctx.resume();
      if ((ctx.state as string) !== "running") {
        await playSilentBuffer(ctx);
        await ctx.resume();
      }
    }
    await ctx.resume();
  }

  function playNote(
    ctx: AudioContext,
    frequency: number,
    startTime: number,
    duration: number,
    volume: number,
  ): void {
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.type = "triangle";
    oscillator.frequency.setValueAtTime(frequency, startTime);

    gainNode.gain.setValueAtTime(0, startTime);
    gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.02);
    gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.start(startTime);
    oscillator.stop(startTime + duration);

    activeOscillators.push(oscillator);
    oscillator.onended = () => {
      const index = activeOscillators.indexOf(oscillator);
      if (index > -1) activeOscillators.splice(index, 1);
    };
  }

  function getChordFrequencies(
    chordSymbol: string,
    baseOctave = 3,
  ): number[] | null {
    const cleaned = normalizeChordSymbol(chordSymbol);
    const chord = Chord.get(cleaned);
    if (chord.empty || !chord.tonic) return null;

    const tonicWithOctave = `${chord.tonic}${baseOctave}`;
    const notesWithOctave = Chord.notes(cleaned, tonicWithOctave);

    const frequencies: number[] = [];
    for (const note of notesWithOctave) {
      const freq = Note.freq(note);
      if (freq) frequencies.push(freq);
    }
    return frequencies.length > 0 ? frequencies : null;
  }

  async function playFrequencies(
    frequencies: number[],
    options: { duration?: number; volume?: number; strumDelay?: number } = {},
  ): Promise<void> {
    const { duration = 2, volume = globalVolume, strumDelay = 0.03 } = options;

    const ctx = getAudioContext();
    await ensureAudioContextRunning(ctx);

    const now = ctx.currentTime + 0.05;
    frequencies.forEach((freq, index) => {
      const noteStart = now + index * strumDelay;
      playNote(ctx, freq, noteStart, duration, volume);
    });
  }

  return {
    async playChord(
      symbol: string,
      options: ChordPlayOptions = {},
    ): Promise<void> {
      const {
        octave = 3,
        duration = 2,
        volume = globalVolume,
        strumDelay = 0.03,
      } = options;
      const frequencies = getChordFrequencies(symbol, octave);
      if (!frequencies) {
        console.warn(`Could not parse chord: ${symbol}`);
        return;
      }
      await playFrequencies(frequencies, { duration, volume, strumDelay });
    },

    async playChordFromFrets(
      frets: (number | null)[],
      options: ChordPlayOptions = {},
    ): Promise<void> {
      const {
        duration = 2,
        volume = globalVolume,
        strumDelay = 0.03,
      } = options;
      const frequencies: number[] = [];

      for (let i = 0; i < frets.length; i++) {
        const fret = frets[i];
        if (fret === null || fret === undefined) continue;

        const openNote = GUITAR_TUNING[i];
        if (!openNote) continue;

        const interval = Interval.fromSemitones(fret);
        const note = Note.transpose(openNote, interval);
        if (!note) continue;

        const freq = Note.freq(note);
        if (freq) frequencies.push(freq);
      }

      if (frequencies.length === 0) {
        console.warn("No playable notes in frets");
        return;
      }

      await playFrequencies(frequencies, { duration, volume, strumDelay });
    },

    async playStringNote(
      stringIndex: number,
      fret: number,
      options: NotePlayOptions = {},
    ): Promise<void> {
      const { duration = 0.5, volume = globalVolume } = options;

      const openNote = GUITAR_TUNING[stringIndex];
      if (!openNote) return;

      const interval = Interval.fromSemitones(fret);
      const note = Note.transpose(openNote, interval);
      if (!note) return;

      const freq = Note.freq(note);
      if (!freq) return;

      await playFrequencies([freq], { duration, volume, strumDelay: 0 });
    },

    stopAll(): void {
      for (const osc of activeOscillators) {
        try {
          osc.stop();
        } catch {
          // Already stopped
        }
      }
      activeOscillators.length = 0;
    },

    setVolume(volume: number): void {
      globalVolume = Math.max(0, Math.min(1, volume));
    },

    dispose(): void {
      this.stopAll();
      removeUnlockListeners();
      if (audioContext && audioContext.state !== "closed") {
        audioContext.close();
      }
      audioContext = null;
    },
  };
}
